import { Axios } from "../../utils/axiosMiddleware";
import { SimpleCatchError } from "../../utils/Helper";

/**
 * Stripe Billing subscription (web-owned). Checkout `mode:subscription` collects
 * the card on Stripe's hosted page; the Customer Portal handles manage/cancel.
 * Both return a hosted `{ url }` the caller redirects to. Provisioning is
 * webhook-driven server-side (invoice.paid) — the client never marks the user
 * subscribed. Returns the response data; undefined on error.
 *
 * Contract confirmed against the backend (GIG-33, extended in GIG-164): the user
 * is derived from the JWT server-side, so the bodies carry only redirect URLs
 * plus the chosen plan —
 *   checkout: { successUrl, cancelUrl, planId? }  (URLs must be absolute)
 *   portal:   { returnUrl }
 * `planId` is our own integer id, never a Stripe price id: a price id from the
 * client would let a user bill themselves at any price in the account. Omitted,
 * the server falls back to the lowest-ordered enabled plan. The server re-checks
 * the plan when it creates the session and returns 409 PLAN_UNAVAILABLE if it
 * was disabled since this page rendered.
 * `successUrl` must land on `/subscription?result=true` to preserve the deep-link
 * watcher the mobile WebView also relies on.
 *
 * `token` is the WebView deep-link JWT (`?token=`). The shared `Axios` interceptor
 * only reads the auth blob from `localStorage`, which is empty inside the mobile
 * CustomWebView, so without this the checkout/portal POST goes out unauthenticated
 * → 401 → the interceptor clears the session and redirects to `/`. When a token is
 * passed we set `Authorization` explicitly on the request (mirroring the amount
 * fetch); on desktop web `token` is undefined and the interceptor's localStorage
 * token is used, so that path is unchanged.
 */
const authConfig = (token) =>
  token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

export const createSubscriptionCheckout = async (payload, token) => {
  try {
    const response = await Axios.post(
      "stripe/subscription/checkout",
      payload,
      authConfig(token)
    );
    return response?.data;
  } catch (e) {
    SimpleCatchError(e);
  }
};

export const createSubscriptionPortal = async (payload, token) => {
  try {
    const response = await Axios.post(
      "stripe/subscription/portal",
      payload,
      authConfig(token)
    );
    return response?.data;
  } catch (e) {
    SimpleCatchError(e);
  }
};
